import { AfterViewInit, Component } from '@angular/core';
import { Customer, Lookup, SubscriptionWarning } from '../app.types';
import { CustomerManagementService } from '../services/customer-management/customer-management.service';
import { combineLatest, forkJoin, merge, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { createDateObject, formatDate, getDateDiff } from 'src/util/date-helpers';
import { UsageAmountsService } from '../services/usage/usage-amounts.service';
import { convert, formatDuration } from 'src/util/conversion-helpers';
import { createNumericalCompare } from 'src/util/array-helpers';
import { LookupService } from '../services/lookup/lookup.service';
import { getUsageThreshold } from 'src/util/threshold-helpers';
import { nullCheckWrap } from 'src/util/function-helper';

type SortConfig = {
  name: string; id: string; accessNum: (a: Customer) => number
}

@Component({
    selector: 'customer-management-page',
    templateUrl: './customer-management-page.component.html',
    styleUrls: ['./customer-management-page.component.scss'],
    standalone: false
})
export class CustomerManagementPageComponent implements AfterViewInit {
  form: FormGroup;

  filtered = false;
  isLoadingResults = true;
  resultsLength: number;

  activeCustomer: Customer = null;
  addingCustomer = false;

  forceTableRefresh = new Subject<null>();

  customers: Customer[];
  customerTypes: Lookup[] = [];

  today = new Date();
  amberWarningDateThreshold = new Date();
  redWarningDateThreshold = new Date();

  initialSearchProperty = "name";

  searchProperties: Lookup[] = [
    { name: "Customer Name", id: "name" },
    { name: "Customer Contact", id: "contact" }
  ]

  dateThresholdMessages : Record<SubscriptionWarning, string> = {
    high: "Tenancy expires within 90 days",
    severe: "Tenancy expires within 30 days",
    outOfContract: "Tenancy Expired",
    none: ""
  };

  sortBy: FormControl = new FormControl("");
  sortAsc: FormControl = new FormControl(true);

  sortOptions: SortConfig[] = [
    { name: "Assets", id: "assets", accessNum: cust => cust?.assetCount ?? 0 },
    { name: "Users", id: "users", accessNum: cust => cust?.combinedUsage?.loggedInUsers ?? 0 },
    { name: "Storage", id: "storage", accessNum: cust => cust?.combinedUsage?.blobStorageUsed ?? 0 },
    { name: "Subscription End Date", id: "subscriptionEndDate", accessNum: cust => new Date(cust?.subscriptionEndDate)?.getTime() ?? 0  }
  ]

  constructor(
    private customerManagementService: CustomerManagementService, 
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private usageAmountsService: UsageAmountsService,
    private lookupService: LookupService
  ) {
    const initialFreeSearchProperty = this.searchProperties.find(x => x.id === this.initialSearchProperty);
    this.amberWarningDateThreshold.setDate(this.amberWarningDateThreshold.getDate() + 90);
    this.redWarningDateThreshold.setDate(this.amberWarningDateThreshold.getDate() + 30);
    this.form = new FormGroup({
        search: new FormControl({property: initialFreeSearchProperty, searchTerm: ""}),
        sortBy: this.sortBy,
        sortAsc: this.sortAsc,
        selectedCustomerTypes: new FormControl([])
    });
  }

  /**
   * On Init
   */
  ngAfterViewInit() {
    this.lookupService.getList("customerType", {})
      .subscribe(res => {
        if(res.results){
          this.customerTypes = res.results;
        }
      });

    merge(
            this.form.valueChanges.pipe(
                debounceTime(500),
                distinctUntilChanged()
            ),
            this.forceTableRefresh
        )
            .pipe(
                startWith({}),
                switchMap((trigger) => {
                    this.isLoadingResults = true;

                    const { property, searchTerm } = this.form.get("search").value;
                    this.filtered = searchTerm != "";

                    const search = this.form.get('selectedCustomerTypes').value.map(x => { 
                        return { type: "customerTypeId", searchString: x.id }});

                    if(searchTerm) {search.push({ type: property.id, searchString: searchTerm });}

                    const getCustomersObs = this.customerManagementService.getCustomers({
                        page: 1,
                        pageSize: 999,
                        orderBy: "name",
                        ascending: true,
                        search: search.length > 0 ? search : null,
                    }).pipe(
                      switchMap(res => {
                        const fixedAmountsRequests = {};
                        
                        res.body.results.forEach(x => fixedAmountsRequests[x.tenantId] = x.subscriptionStartDate);
                        return combineLatest([of(res), this.usageAmountsService.getFixedAmounts(fixedAmountsRequests)]);
                      })
                    );
                    const getMonthlyAmountsObs = this.usageAmountsService.getMonthlyAmounts();

                    return forkJoin([getCustomersObs, getMonthlyAmountsObs, of(trigger.sortBy)])
                }),
                map(([[customerRes, fixedStats], monthlyStats, sortBy]) => {
                  const monthlyStatsMap = new Map(monthlyStats.map(x => [x.tenantId, x]));
                  const fixedStatsMap = new Map(fixedStats.map(x => [x.tenantId, x]));
                  
                  const response = customerRes.body;
                  if (response != null) {
                      this.isLoadingResults = false;
                      this.resultsLength = response.totalNumberOfRecords;

                      const res = response.results.map(x => {
                        return { 
                          ...x, 
                          combinedUsage: { 
                            ...monthlyStatsMap.get(x.tenantId) ?? { minutesStreamed: 0, secondsRedacted: 0, xbaTranscodeMinutes: 0, blobStorageUsed: 0 },
                            ...fixedStatsMap.get(x.tenantId) ?? { loggedInUsers: 0, incidentsCreated: x.subscriptionStartDate ? 0 : null }
                          }
                        }
                      });

                      if(sortBy){
                        const sortOption = this.sortOptions.find(x => x.id === sortBy);
                        if(sortOption) {res.sort(createNumericalCompare(sortOption.accessNum));}
                        if(!this.form.get('sortAsc').value) {res.reverse();}
                      }

                      return res;
                  }
        }),
        catchError(() => {
          this.isLoadingResults = false;
          return of([]);
        })
      )
      .subscribe((data) => {
        if(data){
          this.customers = data.map(customer => {
            const subStartDate = createDateObject(customer.subscriptionStartDate);
            const subEndDate = createDateObject(customer.subscriptionEndDate);
            const getSubscriptionWarningNullSafe = nullCheckWrap((start: Date, end: Date) => this.getSubscriptionWarning(start, end));

            const {assetLimit, storageLimit, incidentCreationLimit, concurrentUsersLimit, liveVideoLimit, redactionLimit} 
              = customer?.subscriptionDetails ?? {};

            const {blobStorageUsed, incidentsCreated, loggedInUsers, minutesStreamed, secondsRedacted} 
              = customer?.combinedUsage ?? {};

            const storageLimitBytes = convert('terabytes', 'bytes', storageLimit);
            const redactionLimitSeconds = convert('minutes', 'seconds', redactionLimit);

            const getSubscriptionPlanNullSafe = nullCheckWrap(this.getSubscriptionPlan);
            const formatDateNullSafe = nullCheckWrap(formatDate);

            return {
              ...customer,
              formattedCreationDate: formatDateNullSafe(customer.createdDate),
              subscriptionPlan: getSubscriptionPlanNullSafe(subStartDate, subEndDate),
              formattedSubStartDate: formatDateNullSafe(customer.subscriptionStartDate),
              formattedSubEndDate: formatDateNullSafe(customer.subscriptionEndDate),
              subscriptionWarning: getSubscriptionWarningNullSafe(subStartDate, subEndDate),
              formattedSubDetails: {
                assetLimit: this.getLimitAmount(assetLimit),
                assetUsage: customer.assetCount,

                storageLimit: this.getLimitAmount(storageLimit, 'TB'),
                storageUsed: (blobStorageUsed === null || blobStorageUsed === undefined) ? null 
                  : `${convert("bytes", "terabytes", blobStorageUsed, 1)} TB`,

                incidentCreationLimit: this.getLimitAmount(incidentCreationLimit),
                incidentsCreated: incidentsCreated,

                concurrentUsersLimit: this.getLimitAmount(concurrentUsersLimit),
                loggedInUsers: loggedInUsers,

                liveVideoLimit: this.getLimitAmount(liveVideoLimit, 'mins'),
                liveVideoStreamed: formatDuration(minutesStreamed, 'minutes'),
                
                redactionLimit: this.getLimitAmount(redactionLimit, 'mins'),
                redactedSeconds: formatDuration(secondsRedacted, 'seconds')
            },
            warningLevels: {
              assets: getUsageThreshold(assetLimit, customer.assetCount),
              storage: getUsageThreshold(storageLimitBytes, blobStorageUsed),
              incidents: getUsageThreshold(incidentCreationLimit, incidentsCreated),
              users: getUsageThreshold(concurrentUsersLimit, loggedInUsers),
              streaming: getUsageThreshold(liveVideoLimit, minutesStreamed),
              redaction: getUsageThreshold(redactionLimitSeconds, secondsRedacted)
            }
          }})
        }
      });
  }

  /**
   * Opens the customer form modal
   */
  openCustomerModal(customer?: Customer) {
    this.addingCustomer = true;
    customer != null ? (this.activeCustomer = customer) : (this.activeCustomer = null);
  }

  navToCustomerEditForm(customer?: Customer){
    this.usageAmountsService.changeCurrentUsage(customer.combinedUsage);
    this.router.navigate(["./edit", customer.id], {relativeTo: this.activatedRoute});
  }

  /**
   * Goes to the device page with a selected customer
   * @param {Customer} customer
   */
  gotoDeviceManagement(customer?: Customer) {
    this.router.navigate(['/device-management'], {
      queryParams: { customer: customer.name, customerId: customer.id },
    });
  }

  /**
   * Close Modal
   */
  modalClosed() {
    this.addingCustomer = false;
    this.activeCustomer = null;
  }

  getLimitAmount(limit: number, units: string = null){
    let amount = limit?.toString();
    if(limit < 0)
      {return "∞"}
    if(units && amount)
      {amount += ` ${units}`}
    return amount;
  }

  sort(id: string) {
    this.sortBy.setValue(id);
  }

  setSortOrder(value: boolean) {
    this.sortAsc.setValue(value);
  }

  resetFilters(){
    this.form.reset();
    const initialFreeSearchProperty = this.searchProperties.find(x => x.id === this.initialSearchProperty);
    this.form.controls['search'].setValue({property: initialFreeSearchProperty, searchTerm: ""});
    this.form.controls['selectedCustomerTypes'].setValue([]);
    this.sort('name');
    this.setSortOrder(true);
  }

  getSubscriptionWarning(subStartDate: Date, subEndDate: Date): SubscriptionWarning {
    if(subEndDate.getTime() < subStartDate.getTime() ||  subEndDate.getTime() < this.today.getTime())
      {return 'outOfContract';}

    if(subEndDate.getTime() < this.redWarningDateThreshold.getTime())
      {return 'severe'}

    if(subEndDate.getTime() < this.amberWarningDateThreshold.getTime())
      {return 'high';}
  }

  getSubscriptionPlan(start: Date, end: Date) {
    const monthsDiff = getDateDiff(start, end, "months");
    return monthsDiff > 11 && monthsDiff < 23 ? 
      `1 Year` : monthsDiff < 12 ?
      `Limited Trial` : `${end.getFullYear()-start.getFullYear()} Years`;
  }
}
